// Worth a look (SCENARIOS E1, PLAYERS.md, PLAN-2026-10-03-research-tools.md
// §6): available players tied to a hole in my roster, each with the fact that
// put it there. `GET /api/leagues/:id/teams/:teamId/suggestions?position=`.
//
// A SET WITH REASONS, NEVER ONE NAME, and never a sentence saying to add
// anybody (docs/decisions/2026-09-12-tools-not-instructions.md): each item is
// the pool's own row — its projection, its matchup, its Add or Claim — and the
// hole it covers, as facts a client puts into words.

import { z } from 'zod';
import { PlayerRowView, PlayerSummary } from './views';

/**
 * Why a player is on the strip: the hole in my roster he covers.
 *
 * - `bye`: a player I start at the position has no game that week.
 * - `out`: a player I start at the position is ruled out.
 * - `short`: fewer can play at the position that week than it has starting
 *   spots, for no one player's reason (nobody rostered there, say).
 * - `depth`: everybody at the position starts and nobody is behind them.
 * - `top`: no hole at all; the highest projected available at a position I
 *   start, which is what the strip shows rather than nothing.
 */
export const SuggestionReason = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('bye'),
    position: z.string(),
    period: z.number().int().min(1),
    label: z.string(),
    player: PlayerSummary,
  }),
  z.object({
    kind: z.literal('out'),
    position: z.string(),
    period: z.number().int().min(1),
    label: z.string(),
    player: PlayerSummary,
  }),
  z.object({
    kind: z.literal('short'),
    position: z.string(),
    period: z.number().int().min(1),
    label: z.string(),
    canPlay: z.number().int().min(0),
    starting: z.number().int().min(1),
  }),
  z.object({
    kind: z.literal('depth'),
    position: z.string(),
    rostered: z.number().int().min(0),
    starting: z.number().int().min(1),
  }),
  z.object({ kind: z.literal('top'), position: z.string() }),
]);
export type SuggestionReason = z.infer<typeof SuggestionReason>;

export const Suggestion = z.object({
  /** The Players row, exactly as the pool serves it: projection, matchup, availability, action. */
  row: PlayerRowView,
  because: SuggestionReason,
});
export type Suggestion = z.infer<typeof Suggestion>;

/** At most this many: a shortlist, not a second list. */
export const SUGGESTIONS_MAX = 5;

export const SuggestionsView = z.object({
  /** The week the figures are for: the one the Players list shows. */
  period: z.object({ index: z.number().int().min(1), label: z.string() }).nullable(),
  /** Every hole found, most pressing first — this week's, then next week's byes, then depth. */
  holes: z.array(SuggestionReason),
  items: z.array(Suggestion).max(SUGGESTIONS_MAX),
  /** The league turned suggestions off: no holes and no items (RULES_SCHEMA §15c). Added 2026-10-29. */
  off: z.boolean().default(false),
});
export type SuggestionsView = z.infer<typeof SuggestionsView>;

export const SuggestionsQuery = z.object({
  /** One position only; absent is every hole. */
  position: z.string().trim().min(1).max(10).optional(),
});
export type SuggestionsQueryShape = z.infer<typeof SuggestionsQuery>;
